import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getAllUnitsForAdmin, bulkUpdateStatus } from '@/lib/admin/units';
import type { PropertyUnit } from '@/lib/units';
import { getDealStageName, getDealStageBadgeClasses } from '@/lib/webhooks/hubspot-deal-sync';
import { Search, Edit, Loader2, CheckSquare, Square } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const AdminUnits = () => {
  const navigate = useNavigate();
  const [units, setUnits] = useState<PropertyUnit[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<PropertyUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [publicationFilter, setPublicationFilter] = useState<string>('all');
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const loadUnits = async () => {
    setLoading(true);
    try {
      const { data, error } = await getAllUnitsForAdmin();
      if (error) {
        console.error('Error loading units:', error);
        // You might want to show a toast/alert here
      } else {
        setUnits(data || []);
      }
    } catch (error) {
      console.error('Error loading units:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnits();
  }, []);

  const filterUnits = useCallback(() => {
    let filtered = [...units];

    // Search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (unit) =>
          unit.unit_number.toLowerCase().includes(query) ||
          unit.unit_type.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((unit) => unit.availability === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((unit) => unit.category === categoryFilter);
    }

    // Publication filter
    if (publicationFilter !== 'all') {
      filtered = filtered.filter((unit) => 
        publicationFilter === 'published' ? unit.is_published : !unit.is_published
      );
    }

    setFilteredUnits(filtered);
  }, [units, searchQuery, statusFilter, categoryFilter, publicationFilter]);

  useEffect(() => {
    filterUnits();
  }, [filterUnits]);

  const handleEditUnit = (unit: PropertyUnit) => {
    trackEvent('admin_unit_edit_opened', { unit_number: unit.unit_number });
    navigate(`/admin/units/${unit.id}/edit`);
  };

  const toggleUnitSelection = (unitId: string) => {
    const newSelection = new Set(selectedUnitIds);
    if (newSelection.has(unitId)) {
      newSelection.delete(unitId);
    } else {
      newSelection.add(unitId);
    }
    setSelectedUnitIds(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedUnitIds.size === filteredUnits.length) {
      setSelectedUnitIds(new Set());
    } else {
      setSelectedUnitIds(new Set(filteredUnits.map((u) => u.id)));
    }
  };

  const handleBulkStatusUpdate = async (status: 'available' | 'reserved' | 'sold') => {
    if (selectedUnitIds.size === 0) return;

    setBulkUpdating(true);
    try {
      const result = await bulkUpdateStatus(Array.from(selectedUnitIds), status);
      if (result.success) {
        await loadUnits();
        setSelectedUnitIds(new Set());
        trackEvent('admin_bulk_unit_status_updated', {
          unit_count: selectedUnitIds.size,
          status,
        });
      } else {
        console.error('Bulk update failed:', result.error);
        // You might want to show a toast/alert here
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
    } finally {
      setBulkUpdating(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'available':
        return 'default';
      case 'reserved':
        return 'secondary';
      case 'sold':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading units...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Unit Management</CardTitle>
          <CardDescription className="text-sm sm:text-base">
            Manage availability, pricing, and details for all {units.length} units
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters and Search */}
          <div className="space-y-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by unit number or type..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>

                {/* Category Filter */}
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="1bed">1 Bedroom</SelectItem>
                    <SelectItem value="2bed">2 Bedroom</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                  </SelectContent>
                </Select>

                {/* Publication Filter */}
                <Select value={publicationFilter} onValueChange={setPublicationFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Draft + Published)</SelectItem>
                    <SelectItem value="published">Published Only</SelectItem>
                    <SelectItem value="draft">Draft Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Actions */}
              {selectedUnitIds.size > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-2 p-3 bg-muted rounded-lg">
                  <span className="text-sm font-medium flex-1">
                    {selectedUnitIds.size} unit(s) selected
                  </span>
                  <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkStatusUpdate('available')}
                      disabled={bulkUpdating}
                      className="flex-1 sm:flex-none"
                    >
                      {bulkUpdating && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <span className="hidden sm:inline">Mark Available</span>
                      <span className="sm:hidden">Available</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkStatusUpdate('reserved')}
                      disabled={bulkUpdating}
                      className="flex-1 sm:flex-none"
                    >
                      {bulkUpdating && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <span className="hidden sm:inline">Mark Reserved</span>
                      <span className="sm:hidden">Reserved</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkStatusUpdate('sold')}
                      disabled={bulkUpdating}
                      className="flex-1 sm:flex-none"
                    >
                      {bulkUpdating && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <span className="hidden sm:inline">Mark Sold</span>
                      <span className="sm:hidden">Sold</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedUnitIds(new Set())}
                      className="flex-1 sm:flex-none"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Card View */}
            <div className="block md:hidden space-y-4">
              {filteredUnits.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No units found matching your filters
                </div>
              ) : (
                filteredUnits.map((unit) => (
                  <Card key={unit.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1">
                          <button
                            onClick={() => toggleUnitSelection(unit.id)}
                            className="p-1 hover:bg-muted rounded flex-shrink-0"
                            aria-label={`Select ${unit.unit_number}`}
                          >
                            {selectedUnitIds.has(unit.id) ? (
                              <CheckSquare className="h-5 w-5" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg truncate">{unit.unit_number}</h3>
                            <p className="text-sm text-muted-foreground">{unit.unit_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-nowrap">
                          <Badge 
                            variant={unit.is_published ? 'default' : 'outline'}
                            className={
                              unit.is_published
                                ? 'bg-status-available hover:bg-status-available/90 text-white border-status-available whitespace-nowrap'
                                : 'bg-status-reserved hover:bg-status-reserved/90 text-white border-status-reserved whitespace-nowrap'
                            }
                          >
                            {unit.is_published ? '✓ Published' : '🟡 Draft'}
                          </Badge>
                          <Badge 
                            variant={getStatusBadgeVariant(unit.availability)}
                            className={
                              unit.availability === 'available'
                                ? 'bg-status-available hover:bg-status-available/90 text-white border-status-available whitespace-nowrap'
                                : unit.availability === 'reserved' 
                                ? 'bg-status-reserved hover:bg-status-reserved/90 text-white border-status-reserved whitespace-nowrap'
                                : unit.availability === 'sold'
                                ? 'bg-status-sold hover:bg-status-sold/90 text-white border-status-sold whitespace-nowrap' 
                                : 'whitespace-nowrap'
                            }
                          >
                            {unit.availability.charAt(0).toUpperCase() +
                              unit.availability.slice(1)}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Category:</span>
                          <Badge variant="outline" className="ml-2">{unit.category}</Badge>
                        </div>
                        <div>
                          <span className="text-muted-foreground">View:</span>
                          <Badge variant="outline" className="ml-2">
                            {unit.view_type.charAt(0).toUpperCase() + unit.view_type.slice(1)}
                          </Badge>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Bedrooms:</span>
                          <span className="ml-2 font-medium">{unit.bedrooms}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Bathrooms:</span>
                          <span className="ml-2 font-medium">{unit.bathrooms}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Price:</span>
                          <span className="ml-2 font-medium">
                            {unit.show_price && unit.price
                              ? formatPrice(unit.price)
                              : 'Hidden'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Deal Stage:</span>
                          <span className="ml-2">
                            {unit.hubspot_deal_stage ? (
                              <Badge 
                                variant="default" 
                                className={`text-xs ${getDealStageBadgeClasses(unit.hubspot_deal_stage)}`}
                              >
                                {getDealStageName(unit.hubspot_deal_stage)}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">N/A</span>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="pt-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditUnit(unit)}
                          className="w-full"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Unit
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block w-full">
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <button
                          onClick={toggleSelectAll}
                          className="p-1 hover:bg-muted rounded"
                          aria-label="Select all"
                        >
                          {selectedUnitIds.size === filteredUnits.length &&
                          filteredUnits.length > 0 ? (
                            <CheckSquare className="h-4 w-4" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[100px]">Unit Number</TableHead>
                      <TableHead className="min-w-[80px] hidden lg:table-cell">Type</TableHead>
                      <TableHead className="min-w-[100px] hidden xl:table-cell">Category</TableHead>
                      <TableHead className="min-w-[100px]">Publication</TableHead>
                      <TableHead className="min-w-[90px]">Status</TableHead>
                      <TableHead className="min-w-[100px] hidden lg:table-cell">Price</TableHead>
                      <TableHead className="min-w-[90px] hidden xl:table-cell">View Type</TableHead>
                      <TableHead className="min-w-[120px] hidden xl:table-cell">Deal Stage</TableHead>
                      <TableHead className="min-w-[80px] hidden xl:table-cell">Bedrooms</TableHead>
                      <TableHead className="min-w-[80px] hidden xl:table-cell">Bathrooms</TableHead>
                      <TableHead className="text-right min-w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {filteredUnits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No units found matching your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUnits.map((unit) => (
                      <TableRow key={unit.id}>
                        <TableCell>
                          <button
                            onClick={() => toggleUnitSelection(unit.id)}
                            className="p-1 hover:bg-muted rounded"
                            aria-label={`Select ${unit.unit_number}`}
                          >
                            {selectedUnitIds.has(unit.id) ? (
                              <CheckSquare className="h-4 w-4" />
                            ) : (
                              <Square className="h-4 w-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium">{unit.unit_number}</TableCell>
                        <TableCell className="hidden lg:table-cell">{unit.unit_type}</TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <Badge variant="outline">{unit.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={unit.is_published ? 'default' : 'outline'}
                            className={
                              unit.is_published
                                ? 'bg-status-available hover:bg-status-available/90 text-white border-status-available whitespace-nowrap'
                                : 'bg-status-reserved hover:bg-status-reserved/90 text-white border-status-reserved whitespace-nowrap'
                            }
                          >
                            {unit.is_published ? '✓ Published' : '🟡 Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={getStatusBadgeVariant(unit.availability)}
                            className={
                              unit.availability === 'available'
                                ? 'bg-status-available hover:bg-status-available/90 text-white border-status-available whitespace-nowrap'
                                : unit.availability === 'reserved' 
                                ? 'bg-status-reserved hover:bg-status-reserved/90 text-white border-status-reserved whitespace-nowrap'
                                : unit.availability === 'sold'
                                ? 'bg-status-sold hover:bg-status-sold/90 text-white border-status-sold whitespace-nowrap' 
                                : 'whitespace-nowrap'
                            }
                          >
                            {unit.availability.charAt(0).toUpperCase() +
                              unit.availability.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {unit.show_price && unit.price
                            ? formatPrice(unit.price)
                            : 'Hidden'}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          <Badge variant="outline">
                            {unit.view_type.charAt(0).toUpperCase() +
                              unit.view_type.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {unit.hubspot_deal_stage ? (
                            <Badge 
                              variant="default" 
                              className={`text-xs ${getDealStageBadgeClasses(unit.hubspot_deal_stage)}`}
                            >
                              {getDealStageName(unit.hubspot_deal_stage)}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">N/A</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">{unit.bedrooms}</TableCell>
                        <TableCell className="hidden xl:table-cell">{unit.bathrooms}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUnit(unit)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {filteredUnits.length} of {units.length} units
            </div>
          </CardContent>
        </Card>
    </>
  );
};

export default AdminUnits;

